import { useEffect, useMemo, useState } from "react";
import { WorkforceMember } from "../../workforce/interfaces/workforces.interface";
import { WorkRecordStatusFilter } from "../component/time-sheet-logs/workRecordStatusFilter";

export interface TimeSheetDateRange {
    start: string | null;
    end: string | null;
}

export interface TimeSheetsQueryFilters {
    page: number;
    pageSize: number;
    workerUid?: string;
    clientUid?: string;
    jobAlias?: string;
    day?: string;
    range?: {
        start: string;
        end: string;
    };
    workStatus?: Exclude<WorkRecordStatusFilter, "all">;
}

const toFilterIso = (value: string | Date | null | undefined) => {
    if (!value) return null;
    if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value.toISOString();
    return value;
};

export const useTimeSheetFilters = () => {
    const [selectedWorker, setSelectedWorker] = useState<WorkforceMember | null>(null);
    const [page, setPage] = useState<number>(1);
    const [pageSize, setPageSize] = useState<number>(10);
    const [dateRange, setDateRange] = useState<TimeSheetDateRange>({
        start: null,
        end: null,
    });
    const [workStatus, setWorkStatus] = useState<WorkRecordStatusFilter>("all");

    useEffect(() => {
        setPage(1);
    }, [selectedWorker, dateRange.start, dateRange.end, pageSize, workStatus]);

    const queryFilters = useMemo<TimeSheetsQueryFilters>(() => {
        const filters: TimeSheetsQueryFilters = {
            page,
            pageSize,
        };

        if (selectedWorker?.uid) {
            filters.workerUid = selectedWorker.uid;
        }

        const start = toFilterIso(dateRange.start);
        const end = toFilterIso(dateRange.end);

        if (start && end) {
            if (start === end) {
                filters.day = start;
            } else {
                filters.range = {
                    start,
                    end,
                };
            }
        }

        if (workStatus !== "all") {
            filters.workStatus = workStatus;
        }

        return filters;
    }, [selectedWorker, dateRange, page, pageSize, workStatus]);

    const clearAllFilters = () => {
        setSelectedWorker(null);
        setDateRange({
            start: null,
            end: null,
        });
        setPageSize(10);
        setPage(1);
        setWorkStatus("all");
    };

    return {
        selectedWorker,
        setSelectedWorker,
        dateRange,
        setDateRange,
        pageSize,
        setPageSize,
        page,
        setPage,
        queryFilters,
        clearAllFilters,
        workStatus,
        setWorkStatus,
    };
};
